'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Button } from '@/components/ui/Button';

interface ServiceHeroProps {
  title: string;
  subtitle?: string;
  bgImage?: string;
  showBreadcrumb?: boolean;
}

export function ServiceHero({
  title,
  subtitle,
  bgImage = 'https://picsum.photos/1920/600?random=50',
  showBreadcrumb = true,
}: ServiceHeroProps) {
  return (
    <div className="relative w-full h-[480px] overflow-hidden">
      {/* Background Image */}
      <Image
        src={bgImage}
        alt={title}
        fill
        className="object-cover"
        priority
        unoptimized
      />

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/55" />

      {/* Content */}
      <div className="relative h-full flex flex-col items-center justify-center px-6 z-10">
        {/* Breadcrumb */}
        {showBreadcrumb && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <Breadcrumb itemClassName="text-white/70 hover:text-white" />
          </motion.div>
        )}

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="font-display text-4xl md:text-5xl lg:text-6xl text-white text-center mb-4 max-w-3xl"
        >
          {title}
        </motion.h1>

        {/* Subtitle */}
        {subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-xl text-white/80 text-center max-w-2xl mb-8"
          >
            {subtitle}
          </motion.p>
        )}

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <Button variant="primary" size="lg">
            Get Free Consultation
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
